/**
 * 炼丹炉面板
 */

import type { PlayerState } from '../engine/Player';
import { RECIPES, type Recipe } from '../data/recipes';
import { calcAlchemyRate, hasIngredients, canCraft, getAlchemyLevel, getAlchemyProgress } from '../engine/AlchemySystem';

export type AlchemyAction = 'craft' | 'complete' | 'close';
export interface AlchemyActionPayload {
  action: AlchemyAction;
  recipeId?: string;
  extraItemId?: string;
}

export class AlchemyPanel {
  private overlay: HTMLElement | null = null;
  private onAction: ((payload: AlchemyActionPayload) => void) | null = null;

  setOnAction(callback: (payload: AlchemyActionPayload) => void): void {
    this.onAction = callback;
  }

  open(player: PlayerState): void {
    if (this.overlay) this.overlay.remove();
    const exp = player.alchemyExp ?? 0;
    const prog = getAlchemyProgress(exp);
    const level = prog.level;

    this.overlay = document.createElement('div');
    this.overlay.className = 'modal-overlay';

    let recipesHtml = '';
    for (const recipe of RECIPES) {
      const rate = calcAlchemyRate(player, recipe);
      const canDo = canCraft(player, recipe);
      const missing = hasIngredients(player, recipe);
      const hasAll = missing.length === 0;

      let ingHtml = '';
      for (const ing of recipe.ingredients) {
        const owned = player.inventory.filter((i) => i.id === ing.itemId).length;
        const enough = owned >= ing.count;
        ingHtml += `<span style="color:${enough ? 'var(--success)' : 'var(--danger)'};font-size:0.8em;">${ing.itemName}×${ing.count}(${owned}) </span>`;
      }

      const statusBadge = recipe.isComplete
        ? '<span style="color:var(--success);font-size:0.75em;">[完整]</span>'
        : '<span style="color:var(--text-gold);font-size:0.75em;">[残缺]</span>';

      const disabled = !canDo || !hasAll;
      const disabledReason = !canDo ? `需要${recipe.minSkill}级` : !hasAll ? '材料不足' : '';

      recipesHtml += `
        <div class="recipe-card">
          <div style="display:flex;justify-content:space-between;align-items:center;">
            <span style="font-weight:bold;">🧪 ${recipe.name} ${statusBadge}</span>
            <span style="font-size:0.75em;color:var(--text-secondary);">产出：${recipe.resultCount}×</span>
          </div>
          <div style="font-size:0.75em;color:var(--text-secondary);margin:2px 0;">${recipe.desc}</div>
          <div>${ingHtml}</div>
          <div style="display:flex;justify-content:space-between;align-items:center;margin-top:6px;">
            <span style="font-size:0.8em;color:${rate >= 0.5 ? 'var(--success)' : rate >= 0.3 ? 'var(--text-gold)' : 'var(--danger)'};">成功率${(rate*100).toFixed(0)}%</span>
            <div style="display:flex;gap:4px;">
              ${!recipe.isComplete
                ? `<button class="btn btn-small" data-action="complete" data-recipe="${recipe.id}">🔍 补全</button>`
                : ''}
              <button class="btn btn-small btn-primary" data-action="craft" data-recipe="${recipe.id}"
                ${disabled ? 'disabled' : ''}>${disabled ? disabledReason : '炼制'}</button>
            </div>
          </div>
        </div>
      `;
    }

    this.overlay.innerHTML = `
      <div class="modal" style="max-width:500px;max-height:80vh;overflow-y:auto;">
        <h2>🔥 炼丹炉</h2>
        <div style="margin-bottom:12px;color:var(--text-secondary);font-size:0.9em;">
          熟练度：<b style="color:var(--text-gold);">${level}级</b>
          <span style="font-size:0.8em;">（总经验${exp}）</span>
          ${player.identityId?.startsWith('sect_alchemy') ? '<span style="color:var(--success);"> [丹宗+15%]</span>' : ''}
          <div style="margin-top:4px;">
            <div class="stat-bar" style="height:6px;">
              <div class="stat-bar-fill exp" style="width:${prog.percent}%"></div>
            </div>
            <span style="font-size:0.7em;">${prog.current}/${prog.needed} 升级</span>
          </div>
        </div>
        <div style="display:flex;flex-direction:column;gap:8px;">
          ${recipesHtml}
        </div>
        <button class="btn btn-small" id="alchemy-close" style="margin-top:12px;">关闭</button>
      </div>
    `;

    document.body.appendChild(this.overlay);

    this.overlay.querySelectorAll('button[data-action="craft"]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const recipeId = (btn as HTMLElement).dataset.recipe!;
        if (this.onAction) this.onAction({ action: 'craft', recipeId });
      });
    });

    this.overlay.querySelectorAll('button[data-action="complete"]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const recipeId = (btn as HTMLElement).dataset.recipe!;
        this.showCompleteDialog(player, recipeId);
      });
    });

    document.getElementById('alchemy-close')?.addEventListener('click', () => {
      this.close();
      if (this.onAction) this.onAction({ action: 'close' });
    });
    this.overlay.addEventListener('click', (e) => {
      if (e.target === this.overlay) { this.close(); if (this.onAction) this.onAction({ action: 'close' }); }
    });
  }

  /** 补全丹方弹窗 */
  private showCompleteDialog(player: PlayerState, recipeId: string): void {
    const recipe = RECIPES.find((r) => r.id === recipeId);
    if (!recipe || recipe.isComplete) return;

    // 列出背包中可尝试的材料
    const materials = player.inventory.filter(
      (i) => i.type === 'material' || i.type === 'consumable'
    );
    const unique = [...new Set(materials.map((i) => i.id))];

    const overlay = document.createElement('div');
    overlay.className = 'modal-overlay';
    overlay.innerHTML = `
      <div class="modal" style="max-width:340px;">
        <h3>🔍 补全「${recipe.name}」</h3>
        <p style="font-size:0.85em;color:var(--text-secondary);">选择一种材料尝试加入丹方（成功则补全，失败则材料消耗）：</p>
        <div style="display:flex;flex-direction:column;gap:6px;margin:12px 0;">
          ${unique.map((id) => {
            const item = materials.find((i) => i.id === id)!;
            return `<button class="btn complete-mat-btn" data-item="${id}">${this.matIcon(item.type)} ${item.name}</button>`;
          }).join('')}
        </div>
        <button class="btn btn-small complete-cancel">取消</button>
      </div>
    `;

    document.body.appendChild(overlay);

    overlay.querySelectorAll('.complete-mat-btn').forEach((btn) => {
      btn.addEventListener('click', () => {
        const itemId = (btn as HTMLElement).dataset.item!;
        overlay.remove();
        if (this.onAction) this.onAction({ action: 'complete', recipeId, extraItemId: itemId });
      });
    });
    overlay.querySelector('.complete-cancel')?.addEventListener('click', () => overlay.remove());
    overlay.addEventListener('click', (e) => { if (e.target === overlay) overlay.remove(); });
  }

  private matIcon(type: string): string {
    return type === 'material' ? '🌿' : '🧪';
  }

  close(): void {
    if (this.overlay) { this.overlay.remove(); this.overlay = null; }
  }

  isOpen(): boolean { return this.overlay !== null; }
}
